import _				from "lodash";
import { Auth } 		from "./Auth";

// Ограничения одной отправки файлов в чат: количество выбранных / перетащенных файлов и их суммарный размер
const MAX_ATTACHMENT_FILES = 100;
const MAX_ATTACHMENTS_TOTAL_SIZE = 25 * 1024 * 1024;
// Максимальное количество файлов после распаковки zip архивов и извлечения base64 ассетов из html (как на сервере в AIAgent.Attachments.mjs)
const MAX_EXTRACTED_FILES = 500;
// Глубина распаковки вложенных zip архивов
const MAX_ZIP_DEPTH = 3;
// base64 данные короче этого размера (иконки 1x1, пустые шрифты) из html не извлекаются
const MIN_HTML_BASE64_LENGTH = 100;
// Количество одновременных HTTP запросов загрузки файлов
const UPLOAD_CONCURRENCY = 4;

// Форматы, которые дашборд принимает как ассеты (инструменты create_*_asset на сервере), и контейнеры, которые разбираются на клиенте
const ATTACHMENT_GROUPS = [
	{codename: 'image', title: 'Images', icon: 'fa-picture-o', extensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg']},
	{codename: 'video', title: 'Videos', icon: 'fa-film', extensions: ['mp4']},
	{codename: 'sound', title: 'Sounds', icon: 'fa-music', extensions: ['mp3']},
	{codename: 'font', title: 'Fonts', icon: 'fa-font', extensions: ['ttf', 'otf', 'woff', 'woff2']},
	{codename: 'model', title: '3D models', icon: 'fa-cube', extensions: ['glb', 'fbx']},
	// Ассет Spine - три файла (.json, .atlas, .png), которые показываются одной миниатюрой. По расширению в группу попадает только атлас,
	// json и png попадают в неё, только когда входят в набор Spine (getPendingSpineKeys или spineKey файла сообщения, который определил сервер)
	{codename: 'spine', title: 'Spine', icon: 'fa-child', extensions: ['atlas']},
	{codename: 'data', title: 'Data', icon: 'fa-file-code-o', extensions: ['json']},
	{codename: 'other', title: 'Unknown files', icon: 'fa-question-circle-o', extensions: []}
];
const CONTAINER_EXTENSIONS = ['zip', 'html', 'htm'];

// Расширение файла по MIME-типу base64 данных из html
const EXTENSIONS_BY_MIME = {
	'image/png': 'png',
	'image/jpeg': 'jpg',
	'image/jpg': 'jpg',
	'image/gif': 'gif',
	'image/webp': 'webp',
	'image/svg+xml': 'svg',
	'video/mp4': 'mp4',
	'audio/mpeg': 'mp3',
	'audio/mp3': 'mp3',
	'audio/wav': 'wav',
	'audio/ogg': 'ogg',
	'font/ttf': 'ttf',
	'font/otf': 'otf',
	'font/woff': 'woff',
	'font/woff2': 'woff2',
	'application/x-font-ttf': 'ttf',
	'application/x-font-otf': 'otf',
	'application/font-woff': 'woff',
	'application/x-font-woff': 'woff',
	'application/font-woff2': 'woff2',
	'model/gltf-binary': 'glb',
	'application/json': 'json'
};

// Библиотека сервиса AIAgent: файлы, присылаемые пользователем в чат (перетаскиванием или кнопкой выбора файлов).
// Файлы разбираются на клиенте: zip архивы распаковываются (DecompressionStream браузера, без сторонних библиотек), из html файлов извлекаются base64 ассеты,
// для картинок и видео создаются миниатюры. Затем каждый файл отправляется по HTTP в роут /ai/chat/attachment, а анализ файлов ИИ агентом
// запускается экшеном AIAgentAttachmentsMessage (компонент AIAgent.vue)
export const AIAgentAttachments = {

	Name: "AI Chat Attachments",

	init() {

		// Миниатюры отправленных файлов для сообщений чата (uploadId -> index -> object url). Хранятся до перезагрузки страницы
		this.AttachmentPreviews = {};
		// Данные миниатюр отправленных наборов Spine (uploadId -> индекс файла атласа -> {json, atlas, imageUrl})
		this.AttachmentSpinePreviews = {};
		this.attachmentId = 0;

	},

	getMaxAttachmentFiles() {

		return MAX_ATTACHMENT_FILES;

	},

	getMaxAttachmentsTotalSize() {

		return MAX_ATTACHMENTS_TOTAL_SIZE;

	},

	// Группы форматов, которые можно использовать как ассеты - все креативы поддерживают все типы ассетов (для подсказки на слое перетаскивания)
	getAttachmentFormatGroups() {

		return _.filter(ATTACHMENT_GROUPS, group => group.extensions.length);

	},

	// Значение атрибута accept диалога выбора файлов
	getAttachmentsAccept() {

		return _.map(_.flatMap(ATTACHMENT_GROUPS, 'extensions').concat(CONTAINER_EXTENSIONS), ext => `.${ext}`).join(',');

	},

	getAttachmentGroup(ext) {

		ext = _.toLower(ext || '');

		return _.find(ATTACHMENT_GROUPS, group => group.extensions.includes(ext)) || _.last(ATTACHMENT_GROUPS);

	},

	// Файлы, сгруппированные по типам в порядке ATTACHMENT_GROUPS: [{group, files}]
	// getSpineKey - ключ набора Spine файла (null - файл не входит в набор): файлы одного набора в группе Spine заменяются одним элементом
	// {spineKey, name, size, spineFiles: {json, atlas, png}}
	groupAttachments(files, getExt = file => file.ext, getSpineKey = () => null) {

		let filesByGroup = _.groupBy(files, file => !_.isNil(getSpineKey(file)) ? 'spine' : this.getAttachmentGroup(getExt(file)).codename);

		return _.compact(_.map(ATTACHMENT_GROUPS, group => {

			let groupFiles = filesByGroup[group.codename];

			if (!groupFiles) return null;

			return {group, files: group.codename === 'spine' ? this.collapseSpineSets(groupFiles, getExt, getSpineKey) : groupFiles};

		}));

	},

	// Файлы группы Spine: файлы одного набора объединяются в один элемент, атласы без набора остаются отдельными файлами
	collapseSpineSets(files, getExt, getSpineKey) {

		let items = [],
			setsByKey = {};

		_.each(files, file => {

			let spineKey = getSpineKey(file);

			if (_.isNil(spineKey)) return items.push(file);

			if (!setsByKey[spineKey]) items.push(setsByKey[spineKey] = {spineKey, name: '', size: 0, spineFiles: {}});

			let spineSet = setsByKey[spineKey],
				ext = getExt(file);

			spineSet.spineFiles[ext === 'atlas' ? 'atlas' : (ext === 'png' ? 'png' : 'json')] = file;
			spineSet.size += file.size || 0;

			if (ext === 'atlas') spineSet.name = file.name.replace(/\.[^.]+$/, '');

		});

		return items;

	},

	// Ключи наборов Spine файлов, добавленных для отправки (id файла -> id файла атласа набора)
	getPendingSpineKeys(entries) {

		let spineKeys = {};

		_.each(this.detectSpineSets(entries), spineSet => _.each(spineSet, entry => spineKeys[entry.id] = spineSet.atlas.id));

		return spineKeys;

	},

	// Наборы файлов Spine среди файлов для отправки: [{json, atlas, png}]. Тот же алгоритм, что на сервере (AIAgent.Attachments.mjs, detectAttachmentsSpineSets):
	// json ищется по имени атласа, затем единственный в той же папке, затем единственный оставшийся. png ищется по имени страницы атласа, а если файлы
	// переименованы после экспорта (страница "spine2.png", файл "paladin.png") - по имени атласа или json
	detectSpineSets(entries) {

		// Папка файла: путь в архиве без имени файла ("archive.zip > hero/hero.png" -> "archive.zip > hero"), у присланных напрямую файлов - пустая строка
		let getDir = entry => (entry.path || '').replace(/(\/| > )[^\/>]*$/, ''),
			getBaseName = entry => _.toLower(entry.name).replace(/\.[^.]+$/, ''),
			usedIds = {},
			sets = [];

		let getFreeEntries = isPart => _.filter(entries, entry => !usedIds[entry.id] && isPart(entry));

		_.each(_.filter(entries, entry => entry.spinePages?.length === 1), atlas => {

			let jsons = getFreeEntries(entry => !!entry.spineJson);

			let sameDirJsons = _.filter(jsons, entry => getDir(entry) === getDir(atlas));

			let json = _.find(sameDirJsons, entry => getBaseName(entry) === getBaseName(atlas))
				|| _.find(jsons, entry => getBaseName(entry) === getBaseName(atlas))
				|| (sameDirJsons.length === 1 ? sameDirJsons[0] : null)
				|| (jsons.length === 1 ? jsons[0] : null);

			if (!json) return;

			let pngs = getFreeEntries(entry => entry.ext === 'png');

			let png = _.find(_.map([_.toLower(atlas.spinePages[0]), getBaseName(atlas) + '.png', getBaseName(json) + '.png'], pngName => {

				let namedPngs = _.filter(pngs, entry => _.toLower(entry.name) === pngName);

				return _.find(namedPngs, entry => getDir(entry) === getDir(atlas)) || namedPngs[0];

			}));

			if (!png) return;

			_.each([json, atlas, png], entry => usedIds[entry.id] = true);

			sets.push({json, atlas, png});

		});

		return sets;

	},

	getFileExtension(name) {

		let match = String(name || '').match(/\.([0-9a-z]+)$/i);

		return match ? match[1].toLowerCase() : '';

	},

	// Проверка ограничений отправки с учётом уже добавленных файлов (pendingFiles - исходные файлы до разбора). Возвращает текст ошибки или null
	checkAttachmentsLimits(files, pendingFiles = []) {

		let allFiles = pendingFiles.concat(files);

		if (allFiles.length > MAX_ATTACHMENT_FILES) return `You can attach up to ${MAX_ATTACHMENT_FILES} files at once`;

		let totalSize = _.sumBy(allFiles, 'size');

		if (totalSize > MAX_ATTACHMENTS_TOTAL_SIZE) return `The total size of the attached files must not exceed ${this.formatAttachmentSize(MAX_ATTACHMENTS_TOTAL_SIZE)} (now ${this.formatAttachmentSize(totalSize)})`;

		return null;

	},

	// Разбор присланных файлов: zip архивы распаковываются, из html извлекаются base64 ассеты. Возвращает {entries, skipped}:
	// entries - файлы для отправки ({id, name, path, size, ext, blob, previewUrl, source}), skipped - файлы, которые отправить нельзя ({name, reason})
	async prepareAttachments(files) {

		let result = {entries: [], skipped: []};

		for (let file of files) await this.addAttachmentFile(result, file, file.name, '', file, 0);

		await this.readSpineAttachments(result.entries);

		if (result.entries.length > MAX_EXTRACTED_FILES) {

			_.each(result.entries.slice(MAX_EXTRACTED_FILES), entry => {

				this.releaseAttachmentPreview(entry);

				result.skipped.push({name: entry.name, reason: `more than ${MAX_EXTRACTED_FILES} files after unpacking`, source: entry.source});

			});

			result.entries = result.entries.slice(0, MAX_EXTRACTED_FILES);

		}

		return result;

	},

	// source - исходный файл (для удаления из списка всех извлечённых из него файлов), path - полный путь файла в архиве ("archive.zip > images/hero.png")
	// или имя html, из которого извлечён файл (пустая строка - файл прислан напрямую)
	async addAttachmentFile(result, blob, name, path, source, depth) {

		let ext = this.getFileExtension(name);

		try {

			if (!blob.size) return result.skipped.push({name, reason: 'empty file', source});

			if (ext === 'zip' && depth < MAX_ZIP_DEPTH) {

				let zipEntries = await this.readZipArchive(blob);

				if (!zipEntries.length) result.skipped.push({name, reason: 'empty zip archive', source});

				for (let zipEntry of zipEntries) {

					if (zipEntry.error) result.skipped.push({name: zipEntry.name, reason: zipEntry.error, source});
					else await this.addAttachmentFile(result, zipEntry.blob, _.last(zipEntry.name.split('/')), `${path || name} > ${zipEntry.name}`, source, depth + 1);

				}

				return;

			}

			if (ext === 'html' || ext === 'htm') {

				let htmlAssets = this.extractHTMLBase64Assets(await blob.text(), name);

				if (!htmlAssets.length) result.skipped.push({name, reason: 'no base64 assets found in the html file', source});

				_.each(htmlAssets, htmlAsset => this.addAttachmentEntry(result, htmlAsset.blob, htmlAsset.name, path || name, source));

				return;

			}

			this.addAttachmentEntry(result, blob, name, path, source);

		} catch (e) {

			result.skipped.push({name, reason: `the file can't be read: ${e?.message || e}`, source});

		}

	},

	// Данные файлов Spine для группировки и миниатюр: у атласа - текст и страницы (spineAtlas, spinePages), у json с данными скелета Spine - сами данные (spineJson).
	// Окончательно формат проверяет сервер
	async readSpineAttachments(entries) {

		for (let entry of entries) {

			if (entry.ext !== 'atlas' && entry.ext !== 'json') continue;

			try {

				let text = await entry.blob.text();

				if (entry.ext === 'atlas') {

					entry.spineAtlas = text;
					entry.spinePages = this.getSpineAtlasPages(text);

				} else {

					let data = JSON.parse(text);

					if (_.isPlainObject(data?.skeleton) && _.isArray(data.bones)) entry.spineJson = data;

				}

			} catch (e) {

				// Неверный json или атлас сервер опишет модели как файл, из которого нельзя создать ассет

			}

		}

	},

	// Имена страниц атласа Spine (как на сервере в AIAgent.Tools.AssetSpine.mjs): страница начинается с имени файла в первой строке или после пустой строки.
	// null - текст не похож на атлас Spine
	getSpineAtlasPages(text) {

		let pages = [],
			isPageStart = true;

		_.each(String(text || '').split(/\r?\n/), line => {

			line = _.trim(line);

			if (!line) return isPageStart = true;

			if (isPageStart) pages.push(line);

			isPageStart = false;

		});

		if (!pages.length || !/^\s*size\s*:/m.test(text) || !_.every(pages, page => /\.(png|jpe?g|webp)$/i.test(page))) return null;

		return pages;

	},

	addAttachmentEntry(result, blob, name, path, source) {

		let ext = this.getFileExtension(name),
			group = this.getAttachmentGroup(ext).codename;

		result.entries.push({
			id: ++this.attachmentId,
			name,
			path,
			size: blob.size,
			ext,
			blob,
			// Миниатюры только у картинок и видео
			previewUrl: group === 'image' || group === 'video' ? URL.createObjectURL(blob) : null,
			source
		});

	},

	releaseAttachmentPreview(entry) {

		if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);

	},

	// Чтение zip архива: [{name, blob} | {name, error}]. Поддерживаются методы сжатия stored и deflate (распаковка - DecompressionStream браузера, см. inflateZipEntry)
	async readZipArchive(blob) {

		let buffer = await blob.arrayBuffer(),
			view = new DataView(buffer),
			bytes = new Uint8Array(buffer),
			decoder = new TextDecoder('utf-8');

		// Запись End of Central Directory ищется с конца файла (после неё может быть комментарий до 64 КБ)
		let eocdOffset = -1;

		for (let i = buffer.byteLength - 22; i >= Math.max(0, buffer.byteLength - 22 - 65535); i--) {

			if (view.getUint32(i, true) === 0x06054b50) {

				eocdOffset = i;

				break;

			}

		}

		if (eocdOffset === -1) throw new Error('invalid zip archive');

		let entriesCount = view.getUint16(eocdOffset + 10, true),
			offset = view.getUint32(eocdOffset + 16, true),
			entries = [];

		for (let i = 0; i < entriesCount; i++) {

			if (view.getUint32(offset, true) !== 0x02014b50) throw new Error('invalid zip archive central directory');

			let flags = view.getUint16(offset + 8, true),
				method = view.getUint16(offset + 10, true),
				crc32 = view.getUint32(offset + 16, true),
				compressedSize = view.getUint32(offset + 20, true),
				uncompressedSize = view.getUint32(offset + 24, true),
				nameLength = view.getUint16(offset + 28, true),
				extraLength = view.getUint16(offset + 30, true),
				commentLength = view.getUint16(offset + 32, true),
				localHeaderOffset = view.getUint32(offset + 42, true),
				// Архиваторы Windows могут записывать пути с обратными слешами
				name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength)).replace(/\\/g, '/');

			offset += 46 + nameLength + extraLength + commentLength;

			// Папки и служебные файлы macOS не нужны
			if (name.endsWith('/') || name.startsWith('__MACOSX/') || _.last(name.split('/')) === '.DS_Store') continue;

			if (flags & 1) {

				entries.push({name, error: 'encrypted files in zip archives are not supported'});

				continue;

			}

			if (compressedSize === 0xFFFFFFFF || uncompressedSize === 0xFFFFFFFF || localHeaderOffset === 0xFFFFFFFF) {

				entries.push({name, error: 'ZIP64 archives are not supported'});

				continue;

			}

			let dataOffset = localHeaderOffset + 30 + view.getUint16(localHeaderOffset + 26, true) + view.getUint16(localHeaderOffset + 28, true),
				data = bytes.subarray(dataOffset, dataOffset + compressedSize);

			if (method === 0) entries.push({name, blob: new Blob([data])});
			else if (method === 8) entries.push({name, blob: await this.inflateZipEntry(data, crc32, uncompressedSize)});
			else entries.push({name, error: `unsupported zip compression method ${method}`});

		}

		return entries;

	},

	// Распаковка данных zip, сжатых deflate. Формат deflate-raw есть в DecompressionStream не во всех браузерах, поэтому данные заворачиваются
	// в gzip: заголовок + те же данные deflate + crc32 и размер распакованных данных (берутся из central directory архива)
	async inflateZipEntry(data, crc32, uncompressedSize) {

		let trailer = new DataView(new ArrayBuffer(8));

		trailer.setUint32(0, crc32, true);
		trailer.setUint32(4, uncompressedSize, true);

		let gzip = new Blob([new Uint8Array([0x1f, 0x8b, 0x08, 0, 0, 0, 0, 0, 0, 0xff]), data, trailer.buffer]);

		return await new Response(gzip.stream().pipeThrough(new DecompressionStream('gzip'))).blob();

	},

	// base64 ассеты html файла (data url в атрибутах, css и js): [{name, blob}]. Повторяющиеся данные извлекаются один раз, остальное содержимое html игнорируется
	extractHTMLBase64Assets(html, htmlName) {

		let regexp = /data:([a-z]+\/[a-z0-9.+-]+)(?:;[a-z0-9-]+=[^;,]*)*;base64,([A-Za-z0-9+/=]+)/gi,
			baseName = htmlName.replace(/\.[^.]+$/, ''),
			knownData = {},
			assets = [],
			match;

		while ((match = regexp.exec(html))) {

			let mime = match[1].toLowerCase(),
				data = match[2];

			if (data.length < MIN_HTML_BASE64_LENGTH || knownData[data]) continue;

			knownData[data] = true;

			let binary = atob(data.substring(0, data.length - data.length % 4)),
				bytes = new Uint8Array(binary.length);

			for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

			assets.push({
				name: `${baseName}-asset-${assets.length + 1}.${EXTENSIONS_BY_MIME[mime] || 'bin'}`,
				blob: new Blob([bytes], {type: mime})
			});

		}

		return assets;

	},

	createAttachmentsUploadId() {

		return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 12)}`;

	},

	// Отправка файлов по HTTP (по одному файлу на запрос, несколько запросов одновременно). onProgress(uploadedCount, totalCount).
	// Возвращает {uploadId, failed} - failed - файлы, которые не удалось отправить ({name, reason}). Миниатюры запоминаются для сообщения чата
	async uploadAttachments(entries, versionId, onProgress = null) {

		let uploadId = this.createAttachmentsUploadId(),
			failed = [],
			uploadedCount = 0,
			queue = _.map(entries, (entry, index) => ({entry, index}));

		this.AttachmentPreviews[uploadId] = {};
		this.AttachmentSpinePreviews[uploadId] = {};

		_.each(queue, ({entry, index}) => {

			if (entry.previewUrl) this.AttachmentPreviews[uploadId][index] = entry.previewUrl;

		});

		// Сервер находит наборы Spine тем же алгоритмом - ключ набора в сообщении чата (spineKey) - индекс файла атласа
		_.each(this.detectSpineSets(entries), spineSet => {

			this.AttachmentSpinePreviews[uploadId][entries.indexOf(spineSet.atlas)] = {json: spineSet.json.spineJson, atlas: spineSet.atlas.spineAtlas, imageUrl: spineSet.png.previewUrl};

		});

		let uploadNext = async () => {

			while (queue.length) {

				let {entry, index} = queue.shift();

				try {

					await this.uploadAttachment(entry, index, uploadId, versionId);

				} catch (e) {

					failed.push({name: entry.name, reason: `upload failed: ${e?.message || e}`});

				}

				uploadedCount++;

				if (onProgress) onProgress(uploadedCount, entries.length);

			}

		};

		await Promise.all(_.times(Math.min(UPLOAD_CONCURRENCY, entries.length), () => uploadNext()));

		return {uploadId, failed};

	},

	uploadAttachment(entry, index, uploadId, versionId) {

		return new Promise((resolve, reject) => {

			let formData = new FormData();

			formData.append("versionId", versionId);
			formData.append("uploadId", uploadId);
			formData.append("index", index + '');
			formData.append("name", entry.name);
			formData.append("path", entry.path || '');
			// Сервер определяет тип загруженного файла по содержимому либо по расширению имени - имя без расширения не прошло бы проверку
			formData.append("file", new File([entry.blob], `${index}.${entry.ext || 'bin'}`, {type: entry.blob.type || 'application/octet-stream'}));

			let xhr = new XMLHttpRequest();

			xhr.open("POST", `${location.protocol}//${SetupSettings["domain"]}/ai/chat/attachment`);

			Auth.authorizeRequest(xhr);

			xhr.onload = () => xhr.status === 200 ? resolve() : reject(new Error(`HTTP ${xhr.status}`));
			xhr.onerror = () => reject(new Error('network error'));

			xhr.send(formData);

		});

	},

	// Миниатюра отправленного файла для сообщения чата (null - миниатюры нет или страница перезагружалась)
	getAttachmentPreviewUrl(uploadId, index) {

		return this.AttachmentPreviews[uploadId]?.[index] || null;

	},

	// Данные миниатюры отправленного набора Spine {json, atlas, imageUrl} (null - страница перезагружалась)
	getAttachmentSpinePreview(uploadId, spineKey) {

		return this.AttachmentSpinePreviews[uploadId]?.[spineKey] || null;

	},

	formatAttachmentSize(size) {

		if (size < 1024) return `${size} B`;
		if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

		return `${(size / 1024 / 1024).toFixed(1)} MB`;

	}

};
